import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    AddRemoveGroupMembersRequestModel,
    AssignRevokeGroupRolesRequestModel,
    BulkResponse,
    CreateGroupModel,
    CreateOrUpdateGroupModel,
    GetAllGroupMembersParams,
    GetAllGroupMembersResponse,
    GetGroupByIdParams,
    GetGroupByIdResponse,
    GetGroupDetailsByIdParams,
    GetGroupDetailsByIdResponse,
    GetGroupsParams,
    GetGroupsResponse,
    ProblemDetails,
    SearchGroupsParams,
} from '../models';

import { useCustomIDSClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Returns a list of groups<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/groups.read
 */
export const useGetGroupsHook = () => {
    const getGroups = useCustomIDSClient<GetGroupsResponse>();

    return useCallback(
        (params?: GetGroupsParams, signal?: AbortSignal) => {
            return getGroups({ url: `/groups`, method: 'GET', params, signal });
        },
        [getGroups],
    );
};

export const getGetGroupsQueryKey = (params?: GetGroupsParams) => {
    return [`/groups`, ...(params ? [params] : [])] as const;
};

export const useGetGroupsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetGroupsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetGroupsQueryKey(params);

    const getGroups = useGetGroupsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>> = ({ signal }) =>
        getGroups(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetGroupsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>>;
export type GetGroupsQueryError = ErrorType<ProblemDetails>;

export function useGetGroups<
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetGroupsParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGroups<
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetGroupsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGroups<
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetGroupsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetGroups<
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetGroupsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetGroupsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates a new group<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/groups.write
 */
export const useCreateGroupHook = () => {
    const createGroup = useCustomIDSClient<GetGroupByIdResponse>();

    return useCallback(
        (createGroupModel: CreateGroupModel, signal?: AbortSignal) => {
            return createGroup({
                url: `/groups`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createGroupModel,
                signal,
            });
        },
        [createGroup],
    );
};

export const useCreateGroupMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateGroupHook>>>,
        TError,
        { data: CreateGroupModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateGroupHook>>>,
    TError,
    { data: CreateGroupModel },
    TContext
> => {
    const mutationKey = ['createGroup'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const createGroup = useCreateGroupHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateGroupHook>>>,
        { data: CreateGroupModel }
    > = (props) => {
        const { data } = props ?? {};

        return createGroup(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type CreateGroupMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateGroupHook>>>>;
export type CreateGroupMutationBody = CreateGroupModel;
export type CreateGroupMutationError = ErrorType<ProblemDetails>;

export const useCreateGroup = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateGroupHook>>>,
            TError,
            { data: CreateGroupModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateGroupHook>>>,
    TError,
    { data: CreateGroupModel },
    TContext
> => {
    return useMutation(useCreateGroupMutationOptions(options), queryClient);
};
/**
 * Returns the basic information of a group<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/groups.read
 */
export const useGetGroupByIdHook = () => {
    const getGroupById = useCustomIDSClient<GetGroupByIdResponse>();

    return useCallback(
        (id: string, params?: GetGroupByIdParams, signal?: AbortSignal) => {
            return getGroupById({ url: `/groups/${id}`, method: 'GET', params, signal });
        },
        [getGroupById],
    );
};

export const getGetGroupByIdQueryKey = (id: string, params?: GetGroupByIdParams) => {
    return [`/groups/${id}`, ...(params ? [params] : [])] as const;
};

export const useGetGroupByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetGroupByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetGroupByIdQueryKey(id, params);

    const getGroupById = useGetGroupByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>> = ({ signal }) =>
        getGroupById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetGroupByIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>>;
export type GetGroupByIdQueryError = ErrorType<ProblemDetails>;

export function useGetGroupById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetGroupByIdParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGroupById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetGroupByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGroupById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetGroupByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetGroupById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetGroupByIdParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupByIdHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetGroupByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates a group<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/groups.write
 */
export const useUpdateGroupHook = () => {
    const updateGroup = useCustomIDSClient<GetGroupByIdResponse>();

    return useCallback(
        (id: string, createOrUpdateGroupModel: CreateOrUpdateGroupModel, signal?: AbortSignal) => {
            return updateGroup({
                url: `/groups/${id}`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: createOrUpdateGroupModel,
                signal,
            });
        },
        [updateGroup],
    );
};

export const useUpdateGroupMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateGroupHook>>>,
        TError,
        { id: string; data: CreateOrUpdateGroupModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateGroupHook>>>,
    TError,
    { id: string; data: CreateOrUpdateGroupModel },
    TContext
> => {
    const mutationKey = ['updateGroup'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateGroup = useUpdateGroupHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateGroupHook>>>,
        { id: string; data: CreateOrUpdateGroupModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return updateGroup(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateGroupMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdateGroupHook>>>>;
export type UpdateGroupMutationBody = CreateOrUpdateGroupModel;
export type UpdateGroupMutationError = ErrorType<ProblemDetails>;

export const useUpdateGroup = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateGroupHook>>>,
            TError,
            { id: string; data: CreateOrUpdateGroupModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateGroupHook>>>,
    TError,
    { id: string; data: CreateOrUpdateGroupModel },
    TContext
> => {
    return useMutation(useUpdateGroupMutationOptions(options), queryClient);
};
/**
 * Deletes a group<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/groups.write
 */
export const useDeleteGroupHook = () => {
    const deleteGroup = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deleteGroup({ url: `/groups/${id}`, method: 'DELETE', signal });
        },
        [deleteGroup],
    );
};

export const useDeleteGroupMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteGroupHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteGroupHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteGroup'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteGroup = useDeleteGroupHook();

    const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useDeleteGroupHook>>>, { id: string }> = (
        props,
    ) => {
        const { id } = props ?? {};

        return deleteGroup(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteGroupMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteGroupHook>>>>;

export type DeleteGroupMutationError = ErrorType<ProblemDetails>;

export const useDeleteGroup = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteGroupHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<Awaited<ReturnType<ReturnType<typeof useDeleteGroupHook>>>, TError, { id: string }, TContext> => {
    return useMutation(useDeleteGroupMutationOptions(options), queryClient);
};
/**
 * Returns the details of a group<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/groups.read
 */
export const useGetGroupDetailsByIdHook = () => {
    const getGroupDetailsById = useCustomIDSClient<GetGroupDetailsByIdResponse>();

    return useCallback(
        (id: string, params?: GetGroupDetailsByIdParams, signal?: AbortSignal) => {
            return getGroupDetailsById({ url: `/groups/${id}/detail`, method: 'GET', params, signal });
        },
        [getGroupDetailsById],
    );
};

export const getGetGroupDetailsByIdQueryKey = (id: string, params?: GetGroupDetailsByIdParams) => {
    return [`/groups/${id}/detail`, ...(params ? [params] : [])] as const;
};

export const useGetGroupDetailsByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetGroupDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetGroupDetailsByIdQueryKey(id, params);

    const getGroupDetailsById = useGetGroupDetailsByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>> = ({ signal }) =>
        getGroupDetailsById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetGroupDetailsByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>
>;
export type GetGroupDetailsByIdQueryError = ErrorType<ProblemDetails>;

export function useGetGroupDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetGroupDetailsByIdParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGroupDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetGroupDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetGroupDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetGroupDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetGroupDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetGroupDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetGroupDetailsByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetGroupDetailsByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns a list of all members of a group<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/groups.read
 */
export const useGetAllGroupMembersHook = () => {
    const getAllGroupMembers = useCustomIDSClient<GetAllGroupMembersResponse>();

    return useCallback(
        (id: string, params?: GetAllGroupMembersParams, signal?: AbortSignal) => {
            return getAllGroupMembers({ url: `/groups/${id}/members`, method: 'GET', params, signal });
        },
        [getAllGroupMembers],
    );
};

export const getGetAllGroupMembersQueryKey = (id: string, params?: GetAllGroupMembersParams) => {
    return [`/groups/${id}/members`, ...(params ? [params] : [])] as const;
};

export const useGetAllGroupMembersQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetAllGroupMembersParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetAllGroupMembersQueryKey(id, params);

    const getAllGroupMembers = useGetAllGroupMembersHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>> = ({ signal }) =>
        getAllGroupMembers(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllGroupMembersQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>
>;
export type GetAllGroupMembersQueryError = ErrorType<ProblemDetails>;

export function useGetAllGroupMembers<
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetAllGroupMembersParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllGroupMembers<
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetAllGroupMembersParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllGroupMembers<
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetAllGroupMembersParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetAllGroupMembers<
    TData = Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetAllGroupMembersParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllGroupMembersHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetAllGroupMembersQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Assigns and removes group members in bulk<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/groups.write
 */
export const useAddRemoveGroupMembersHook = () => {
    const addRemoveGroupMembers = useCustomIDSClient<BulkResponse>();

    return useCallback(
        (id: string, addRemoveGroupMembersRequestModel: AddRemoveGroupMembersRequestModel, signal?: AbortSignal) => {
            return addRemoveGroupMembers({
                url: `/groups/${id}/members/bulk`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: addRemoveGroupMembersRequestModel,
                signal,
            });
        },
        [addRemoveGroupMembers],
    );
};

export const useAddRemoveGroupMembersMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useAddRemoveGroupMembersHook>>>,
        TError,
        { id: string; data: AddRemoveGroupMembersRequestModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useAddRemoveGroupMembersHook>>>,
    TError,
    { id: string; data: AddRemoveGroupMembersRequestModel },
    TContext
> => {
    const mutationKey = ['addRemoveGroupMembers'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const addRemoveGroupMembers = useAddRemoveGroupMembersHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useAddRemoveGroupMembersHook>>>,
        { id: string; data: AddRemoveGroupMembersRequestModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return addRemoveGroupMembers(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type AddRemoveGroupMembersMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useAddRemoveGroupMembersHook>>>
>;
export type AddRemoveGroupMembersMutationBody = AddRemoveGroupMembersRequestModel;
export type AddRemoveGroupMembersMutationError = ErrorType<ProblemDetails>;

export const useAddRemoveGroupMembers = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useAddRemoveGroupMembersHook>>>,
            TError,
            { id: string; data: AddRemoveGroupMembersRequestModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useAddRemoveGroupMembersHook>>>,
    TError,
    { id: string; data: AddRemoveGroupMembersRequestModel },
    TContext
> => {
    return useMutation(useAddRemoveGroupMembersMutationOptions(options), queryClient);
};
/**
 * Assigns and removes roles to a group in bulk<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/groups.write
 */
export const useAssignRevokeGroupRolesHook = () => {
    const assignRevokeGroupRoles = useCustomIDSClient<BulkResponse>();

    return useCallback(
        (id: string, assignRevokeGroupRolesRequestModel: AssignRevokeGroupRolesRequestModel, signal?: AbortSignal) => {
            return assignRevokeGroupRoles({
                url: `/groups/${id}/roles/bulk`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: assignRevokeGroupRolesRequestModel,
                signal,
            });
        },
        [assignRevokeGroupRoles],
    );
};

export const useAssignRevokeGroupRolesMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useAssignRevokeGroupRolesHook>>>,
        TError,
        { id: string; data: AssignRevokeGroupRolesRequestModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useAssignRevokeGroupRolesHook>>>,
    TError,
    { id: string; data: AssignRevokeGroupRolesRequestModel },
    TContext
> => {
    const mutationKey = ['assignRevokeGroupRoles'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const assignRevokeGroupRoles = useAssignRevokeGroupRolesHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useAssignRevokeGroupRolesHook>>>,
        { id: string; data: AssignRevokeGroupRolesRequestModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return assignRevokeGroupRoles(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type AssignRevokeGroupRolesMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useAssignRevokeGroupRolesHook>>>
>;
export type AssignRevokeGroupRolesMutationBody = AssignRevokeGroupRolesRequestModel;
export type AssignRevokeGroupRolesMutationError = ErrorType<ProblemDetails>;

export const useAssignRevokeGroupRoles = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useAssignRevokeGroupRolesHook>>>,
            TError,
            { id: string; data: AssignRevokeGroupRolesRequestModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useAssignRevokeGroupRolesHook>>>,
    TError,
    { id: string; data: AssignRevokeGroupRolesRequestModel },
    TContext
> => {
    return useMutation(useAssignRevokeGroupRolesMutationOptions(options), queryClient);
};
/**
 * Returns a list of groups, based on the given search term<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/groups.read
 */
export const useSearchGroupsHook = () => {
    const searchGroups = useCustomIDSClient<GetGroupsResponse>();

    return useCallback(
        (params?: SearchGroupsParams, signal?: AbortSignal) => {
            return searchGroups({ url: `/groups/search`, method: 'GET', params, signal });
        },
        [searchGroups],
    );
};

export const getSearchGroupsQueryKey = (params?: SearchGroupsParams) => {
    return [`/groups/search`, ...(params ? [params] : [])] as const;
};

export const useSearchGroupsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchGroupsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getSearchGroupsQueryKey(params);

    const searchGroups = useSearchGroupsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>> = ({ signal }) =>
        searchGroups(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SearchGroupsQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>>;
export type SearchGroupsQueryError = ErrorType<ProblemDetails>;

export function useSearchGroups<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | SearchGroupsParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchGroups<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchGroupsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSearchGroups<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchGroupsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useSearchGroups<
    TData = Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: SearchGroupsParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useSearchGroupsHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useSearchGroupsQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}
